import React from 'react';
import Button from './Button.jsx';
import './FormButton.css';

/**
 * FormButton component with ArbiWin design system integration
 * Specialized button for form submissions with enhanced loading states
 * Based on registration form design pattern
 *
 * @param {Object} props - FormButton properties
 * @param {string} props.type - Button type: 'submit', 'button', 'reset'
 * @param {string} props.variant - Button variant: 'primary', 'secondary', 'danger'
 * @param {string} props.size - Button size: 'small', 'medium', 'large'
 * @param {boolean} props.loading - Show loading state
 * @param {boolean} props.disabled - Disable button
 * @param {boolean} props.fullWidth - Make button full width
 * @param {string} props.loadingText - Text to show when loading
 * @param {Function} props.onClick - Click handler
 * @param {React.ReactNode} props.children - Button content
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.testId - Test ID for testing
 * @param {React.ReactNode} props.icon - Icon to display
 * @param {string} props.iconPosition - Icon position: 'start', 'end'
 * @returns {JSX.Element} FormButton component
 */
const FormButton = function FormButton({
  type = 'submit',
  variant = 'primary',
  size = 'large', // Default to large for form buttons (matches registration design)
  loading = false,
  disabled = false,
  fullWidth = true, // Default to full width for form buttons
  loadingText,
  onClick,
  children,
  className = '',
  testId,
  icon,
  iconPosition = 'start',
  ...rest
}) {

  const formButtonClasses = [
    'arbiwin-form-button',
    'arbiwin-form-button--' + variant,
    'arbiwin-form-button--' + size,
    loading ? 'arbiwin-form-button--loading' : '',
    disabled ? 'arbiwin-form-button--disabled' : '',
    fullWidth ? 'arbiwin-form-button--full-width' : '',
    icon ? 'arbiwin-form-button--has-icon' : '',
    icon && iconPosition ? 'arbiwin-form-button--icon-' + iconPosition : '',
    className
  ].filter(Boolean).join(' ');

  const buttonContent = loading && loadingText ? loadingText : children;

  const renderIcon = function() {
    if (!icon || loading) return null;

    return (
      <span
        className={'arbiwin-form-button__icon arbiwin-form-button__icon--' + iconPosition}
        aria-hidden="true"
      >
        {icon}
      </span>
    );
  };

  const renderContent = function() {
    if (iconPosition === 'start') {
      return (
        <>
          {renderIcon()}
          <span className="arbiwin-form-button__text">{buttonContent}</span>
        </>
      );
    } else {
      return (
        <>
          <span className="arbiwin-form-button__text">{buttonContent}</span>
          {renderIcon()}
        </>
      );
    }
  };

  return (
    <Button
      type={type}
      variant={variant}
      size={size}
      loading={loading}
      disabled={disabled}
      fullWidth={fullWidth}
      onClick={onClick}
      className={formButtonClasses}
      testId={testId || 'arbiwin-form-button'}
      {...rest}
    >
      {renderContent()}
    </Button>
  );
};

/**
 * FormButtonGroup component for grouping form action buttons
 *
 * @param {Object} props - FormButtonGroup properties
 * @param {React.ReactNode} props.children - Form buttons
 * @param {string} props.align - Button alignment: 'left', 'center', 'right', 'space-between'
 * @param {string} props.gap - Gap between buttons: 'small', 'medium', 'large'
 * @param {boolean} props.stack - Stack buttons vertically on mobile
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.testId - Test ID for testing
 * @returns {JSX.Element} FormButtonGroup component
 */
const FormButtonGroup = function FormButtonGroup({
  children,
  align = 'right',
  gap = 'medium',
  stack = true,
  className = '',
  testId
}) {

  const groupClasses = [
    'arbiwin-form-button-group',
    'arbiwin-form-button-group--align-' + align,
    'arbiwin-form-button-group--gap-' + gap,
    stack ? 'arbiwin-form-button-group--stack' : '',
    className
  ].filter(Boolean).join(' ');

  return (
    <div
      className={groupClasses}
      data-testid={testId || 'arbiwin-form-button-group'}
    >
      {children}
    </div>
  );
};

/**
 * FormSubmitButton component - Pre-configured submit button
 *
 * @param {Object} props - FormSubmitButton properties
 * @param {boolean} props.loading - Show loading state
 * @param {boolean} props.disabled - Disable button
 * @param {string} props.loadingText - Text to show when loading
 * @param {React.ReactNode} props.children - Button content
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} FormSubmitButton component
 */
const FormSubmitButton = function FormSubmitButton({
  loading = false,
  disabled = false,
  loadingText = 'Изпращане...',
  children = 'Изпрати',
  className = '',
  ...rest
}) {
  return (
    <FormButton
      type="submit"
      variant="primary"
      size="large"
      fullWidth={true}
      loading={loading}
      disabled={disabled}
      loadingText={loadingText}
      className={'arbiwin-form-submit-button ' + className}
      {...rest}
    >
      {children}
    </FormButton>
  );
};

/**
 * FormCancelButton component - Pre-configured cancel button
 *
 * @param {Object} props - FormCancelButton properties
 * @param {Function} props.onClick - Click handler
 * @param {React.ReactNode} props.children - Button content
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} FormCancelButton component
 */
const FormCancelButton = function FormCancelButton({
  onClick,
  children = 'Отказ',
  className = '',
  ...rest
}) {
  return (
    <FormButton
      type="button"
      variant="secondary"
      size="large"
      fullWidth={false}
      onClick={onClick}
      className={'arbiwin-form-cancel-button ' + className}
      {...rest}
    >
      {children}
    </FormButton>
  );
};

/**
 * FormResetButton component - Pre-configured reset button
 *
 * @param {Object} props - FormResetButton properties
 * @param {Function} props.onClick - Click handler (optional, can use type="reset")
 * @param {React.ReactNode} props.children - Button content
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} FormResetButton component
 */
const FormResetButton = function FormResetButton({
  onClick,
  children = 'Нулиране',
  className = '',
  ...rest
}) {
  return (
    <FormButton
      type={onClick ? 'button' : 'reset'}
      variant="secondary"
      size="medium"
      fullWidth={false}
      onClick={onClick}
      className={'arbiwin-form-reset-button ' + className}
      {...rest}
    >
      {children}
    </FormButton>
  );
};

FormButton.displayName = 'FormButton';
FormButtonGroup.displayName = 'FormButtonGroup';
FormSubmitButton.displayName = 'FormSubmitButton';
FormCancelButton.displayName = 'FormCancelButton';
FormResetButton.displayName = 'FormResetButton';

export default FormButton;
export { FormButtonGroup, FormSubmitButton, FormCancelButton, FormResetButton };
